import type { ReactElement } from 'react';
import type { AppConfig } from '../api/types';
import { Link } from 'react-aria-components';
import { useWorkspaces } from '../api/queries';
import { useSession } from '../app/session-context';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { CopyButton } from '../ui/CopyButton';
import { PageHeader } from '../ui/PageHeader';
import { Stack } from '../ui/Stack';
import { useDocumentTitle } from '../useDocumentTitle';
import { PRODUCT_NAME } from '@tallyframe/core';

/** Deliberately not a token. Nothing on this page ever fetches, displays or
 *  embeds a real one — a token that appears in a browser page is a token in
 *  the browser's history, the screenshot and the support ticket. */
const TOKEN_PLACEHOLDER = 'PASTE_YOUR_TOKEN_HERE';

const H2 = 'm-0 mb-2 text-lg font-semibold text-ink';
const LINK = 'text-ink underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink';
const INLINE_CODE = 'font-mono text-sm bg-desk border border-solid border-line rounded-control px-1 break-words';

const MINT_COMMAND = 'pnpm token:mint';

/** Shown before the app config has loaded, or if it never does. */
const DEFAULT_ENDPOINT = 'http://127.0.0.1:4317/mcp';

/** The MCP client config snippet, under the `reportpad` server key (spec
 *  §9.2, §10.1 rule 6) — the one piece of agent-facing config this screen
 *  actually shows, over the workspace's own endpoint. */
export function agentConfig(endpoint: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        reportpad: {
          type: 'http',
          url: endpoint,
          headers: { Authorization: `Bearer ${TOKEN_PLACEHOLDER}` }
        }
      }
    },
    null,
    2
  );
}

/** `useWorkspaces()` has no `enabled` option, so it lives in a child rendered
 *  only in accounts mode; admin mode has no `/api/workspaces` route. */
function WorkspaceLine({ workspaceId }: { workspaceId: string }): ReactElement | null {
  const workspaces = useWorkspaces();
  const name = workspaces.data?.find((w) => w.id === workspaceId)?.name;
  return name ? (
    <p className="m-0 text-md">
      Your agent will see <strong>{name}</strong>.
    </p>
  ) : null;
}

/**
 * Connect an agent.
 *
 * Three steps, nothing else (spec §9.2): mint a token, paste the config, try
 * a prompt. What the person can do, not how the system works — the
 * transport, CSRF, hashing and OAuth notes that used to live here belong in
 * the README instead.
 */
export function ConnectRoute(props: { config: AppConfig | null }): ReactElement {
  useDocumentTitle('Connect an agent');
  const { config } = props;
  const workspaceId = useSession().snapshot.workspaceId ?? '';
  const endpoint = config?.mcpEndpoint ?? DEFAULT_ENDPOINT;
  const snippet = agentConfig(endpoint);
  // Gate on accounts too: the server never sends mcpOauth in admin mode.
  const oauth = config?.auth === 'accounts' && config.mcpOauth === true;

  return (
    <div className="p-6">
      <Stack gap={6} className="max-w-3xl">
        <PageHeader
          title="Connect an agent"
          description={`${PRODUCT_NAME} has no built-in AI chat. Your own agent is the AI interface — connect it in three steps.`}
        />

        {config?.auth === 'accounts' ? <WorkspaceLine workspaceId={workspaceId} /> : null}

        {oauth ? (
          <>
            <Card>
              <div className="flex flex-row flex-wrap items-center gap-2">
                <h2 className={H2}>Add {PRODUCT_NAME} as a connector</h2>
                <Badge tone="info">Recommended</Badge>
              </div>
              <p className="mt-0 text-md">
                In claude.ai or ChatGPT, add a custom connector with this URL, then sign in and approve.
              </p>
              <div className="flex flex-row flex-wrap items-center gap-2">
                <code className={INLINE_CODE}>{endpoint}</code>
                <CopyButton value={endpoint} label="Copy URL" />
              </div>
              <p className="mb-0 text-md">
                <Link href="/account/agents" className={LINK}>
                  Manage connected agents
                </Link>
              </p>
            </Card>
            <h2 className={H2}>Use an API token</h2>
          </>
        ) : null}

        {/* `role="list"` keeps list semantics where a reset strips them
            (Safari/VoiceOver drop them from `list-style: none`). */}
        <ol role="list" className="m-0 p-0 list-none flex flex-col gap-4">
          <li>
            <Card>
              <h2 className={H2}>Mint a token</h2>
              <p className="mt-0 text-md">
                <Link href={`/w/${encodeURIComponent(workspaceId)}/settings/tokens`} className={LINK}>
                  Create a token in Workspace settings
                </Link>
                . It is shown once, so copy it straight away.
              </p>
              {config?.auth === 'accounts' ? null : (
                <>
                  <p className="text-md">Or run this in the repository root. It prints the token once.</p>
                  <div className="flex flex-row flex-wrap items-center gap-2">
                    <code className={INLINE_CODE}>{MINT_COMMAND}</code>
                    <CopyButton value={MINT_COMMAND} label="Copy command" />
                  </div>
                </>
              )}
            </Card>
          </li>

          <li>
            <Card>
              <h2 className={H2}>Paste this config</h2>
              <p className="mt-0 text-md">
                Replace <code className={INLINE_CODE}>{TOKEN_PLACEHOLDER}</code> with the token you just minted, in your
                agent&apos;s MCP configuration.
              </p>
              <pre role="region" tabIndex={0} aria-label="MCP client configuration" className="m-0 p-3 overflow-x-auto whitespace-pre rounded-control bg-ink text-surface font-mono text-sm outline-none [&:focus-visible]:outline [&:focus-visible]:outline-solid [&:focus-visible]:outline-2 [&:focus-visible]:outline-offset-2 [&:focus-visible]:outline-ink">
                <code>{snippet}</code>
              </pre>
              <div className="mt-3 flex flex-row items-center gap-2">
                <CopyButton value={snippet} label="Copy" />
              </div>
            </Card>
          </li>

          <li>
            <Card>
              <h2 className={H2}>Try a first prompt</h2>
              <p className="mb-0 text-md">
                Ask your agent to list the reports in this workspace, and confirm it gets an answer back.
              </p>
            </Card>
          </li>
        </ol>
      </Stack>
    </div>
  );
}
